import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing8, Spacing16, Spacing24, Spacing28, Spacing40, Spacing120 } from 'autogen/design-tokens/tokens';
import { GetConfirmCancellationModal, type MatasPlusPendingConfirmCancellationViewModel } from 'autogen/swagger/all';
import {
    PendingCancellationCancelButton,
    PendingCancellationConfirmButton
} from 'autogen/translation-keys/trans-website-matasplus';
import { Button } from 'components/Button';
import LinkActionButton from 'components/Button/LinkActionButton';
import { Typography } from 'components/generic/typography';
import Loader from 'components/Loader/Loader';
import ActionFooter from 'components/quickView/components/ActionFooter';
import format from 'helpers/translations';
import { breakpoint, sizes } from 'variables';

export type TerminatePendingQuickViewProps = {
    onClose: (boolean) => void;
};

const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const Content = styled.div`
    padding: 0 ${Spacing24} ${Spacing24};

    ${breakpoint.up(sizes.md)} {
        padding: 0 ${Spacing40} ${Spacing28};
    }
`;

const StyledHeadline = styled(Typography.Headline)`
    margin-bottom: ${Spacing16};
`;

const StyledActionFooter = styled(ActionFooter)`
    display: grid;
    column-gap: ${Spacing8};
    grid-template-columns: repeat(2, 1fr);
`;

const StyledSpinner = styled(Loader)`
    margin: ${Spacing120} auto;
`;

export default function TerminatePendingQuickView(props: TerminatePendingQuickViewProps) {
    const { onClose } = props;
    const [data, setData] = useState<MatasPlusPendingConfirmCancellationViewModel | undefined>();
    const { t } = useTranslation();

    // Effect for initial data
    useEffect((): void => {
        const fetchData = async () => {
            const response = await GetConfirmCancellationModal();
            setData(response);
        };

        fetchData();
    }, []);

    if (data) {
        return (
            <FlexContainer>
                <Content>
                    <StyledHeadline size="L">{data.header}</StyledHeadline>
                    <Typography.Body as="div">{format(data.content)}</Typography.Body>
                </Content>
                <StyledActionFooter size="lg">
                    <Button variant="secondary" size="lg" onClick={() => onClose(false)}>
                        {t(PendingCancellationCancelButton)}
                    </Button>
                    <LinkActionButton variant="primary" size="lg" action={data.unsubscribeLink.linkAction}>
                        {t(PendingCancellationConfirmButton)}
                    </LinkActionButton>
                </StyledActionFooter>
            </FlexContainer>
        );
    }

    return <StyledSpinner size={24} />;
}
